import { Action, Types } from './types';

interface ChangesMap<T> {
  [id: string]: T;
}

export interface UpdatesState<T> {
  changes: ChangesMap<T>;
  new: ChangesMap<T>;
}

function updateChangesMap<T>(map: ChangesMap<T>, id: string, changes: T): ChangesMap<T> {
  const updatedItem: T = Object.assign({}, map[id], changes);
  return {
    ...map,
    [id]: updatedItem,
  };
}

export const TEMP_ID_PREFIX = 'TemporaryId:';
export const nextNewId = (ids: string[]): string => {
  const maxPresent = Math.max(-1, ...ids.map(id => Number(id.split(TEMP_ID_PREFIX)[1])));
  return `${TEMP_ID_PREFIX}${maxPresent + 1}`;
};

function addNewLocalEntity<T>(state: UpdatesState<T>, localId: string, entity: T): UpdatesState<T> {
  return {
    ...state,
    new: {
      ...state.new,
      [localId]: entity,
    },
  };
}

export function updateLocal<T>(state: UpdatesState<T>, id: string, entity: T): UpdatesState<T> {
  return Object.keys(state.new).indexOf(id) > -1
    ? {
        ...state,
        new: updateChangesMap<T>(state.new, id, entity),
      }
    : {
        ...state,
        changes: updateChangesMap<T>(state.changes, id, entity),
      };
}

export function updatesReducer<T, S>(service: S) {
  const initialState: UpdatesState<T> = {
    changes: {},
    new: {},
  };

  return (state: UpdatesState<T> = initialState, action: Action<T, S>): UpdatesState<T> => {
    if (action.service !== service) {
      return state;
    }

    switch (action.type) {
      case Types.CREATE_LOCAL_WITH_TEMPORARY_ID:
        return addNewLocalEntity(state, action.payload.id, action.payload.entity);
      case Types.CREATE_LOCAL:
        return addNewLocalEntity(state, nextNewId(Object.keys(state.new)), action.payload.entity);

      case Types.UPDATE_LOCAL:
        return updateLocal(state, action.payload.id, action.payload.entity);

      case Types.DISCARD_LOCAL:
      case Types.UPDATE_COMPLETED:
        const updatedChanges = { ...state.changes };
        delete updatedChanges[action.payload.id];
        const updatedNew = { ...state.new };
        delete updatedNew[action.payload.id];

        return {
          ...state,
          changes: updatedChanges,
          new: updatedNew,
        };

      case Types.CREATE_COMPLETED:
        const unsavedNew = { ...state.new };
        delete unsavedNew[action.payload.tempId];
        return {
          ...state,
          new: unsavedNew,
        };

      case Types.RESTORE_UPDATES:
        return action.payload;

      case Types.DISCARD_ALL_LOCAL:
      case Types.CLEAR_ALL:
        return initialState;

      default:
        return state;
    }
  };
}
